/**
 * PostProcessConfig - effect identifiers, render order, typed per-effect options, and authoring defaults for WebGL/WebGPU stacks.
 */

import * as THREE from 'three';

import type { BaseEffectConfig } from './IPostProcessEffect.js';

/**
 * String identifiers for post-processing effects. Use with {@link DefaultPostprocessEffectConfigs} and stack entries typed as {@link BaseEffectConfig} subclasses.
 */
export const PostProcessPass = {
  /** Bloom / halation on bright regions. */
  Bloom: 'bloom',
  /** Screen-space ambient occlusion (SSAO on WebGL, GTAO on WebGPU). */
  AO: 'ao',
  /** HDR tone mapping and exposure control. */
  ToneMapping: 'toneMapping',
  /** Display-referred colour grading controls (WebGPU only for now). */
  ColorGrading: 'colorGrading',
  /** Legacy full-screen anti-aliasing (FXAA/SMAA depending on backend). */
  AntiAliasing: 'antiAliasing',
  /** Object or group outline pass. */
  ObjectOutline: 'objectOutline',
  /** Depth-of-field blur from depth and camera parameters. */
  DepthOfField: 'depthOfField',
  /** Pixel block quantisation with optional edge emphasis. */
  Pixelation: 'pixelation',
  /** CRT-style scanlines, vignette, and colour quantisation. */
  Retro: 'retro',
  /** Screen-space reflections. */
  SSR: 'ssr',
} as const;

export type PostProcessPassType = typeof PostProcessPass[keyof typeof PostProcessPass];

/**
 * Rendering order for post-processing effects.
 * Lower numbers render first. Effects are applied in this order to the scene output.
 */
export const PostProcessRenderOrder = {
  /** AO (SSAO/GTAO) - applied first to scene output */
  AO: 10,
  /** Bloom - applied after AO */
  Bloom: 20,
  /** Tone mapping - applied after bloom */
  ToneMapping: 30,
  /** Colour grading - applied after tone mapping */
  ColorGrading: 35,
  /** Object outline - applied after tone mapping and colour grading (in sRGB space) */
  ObjectOutline: 40,
  /** Retro / CRT-style stack (early stylisation pass) */
  Retro: 45,
  /** Pixelation */
  Pixelation: 54,
  /** Depth of field */
  DepthOfField: 55,
  /** Screen-space reflections */
  SSR: 59,
  /** Legacy anti-aliasing effect (FXAA/SMAA depending on backend) */
  AntiAliasing: 72,
} as const;

// ============= Bloom Effect =============

/**
 * Bloom adds a soft glow around pixels above a luminance threshold, with optional selective emissive paths on WebGPU.
 */
export interface BloomEffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.Bloom;
  /** Bloom strength/intensity */
  strength?: number;
  /** Luminance threshold for bloom */
  threshold?: number;
  /** Luminance smoothing (softness of threshold transition) */
  smoothing?: number;
  /** Bloom blur radius (0-1) */
  radius?: number;
  /** Use mipmap blur (WebGL only) */
  mipmapBlur?: boolean;
  /** Number of MIP levels (WebGL only) */
  levels?: number;
  /** Only bloom emissive materials (WebGPU only, requires) */
  selectiveEmissive?: boolean;
  /** Exclude background/skybox from bloom */
  ignoreBackground?: boolean;
}

// ============= AO (Ambient Occlusion) Effect =============
// WebGL: Uses SSAO (Screen Space Ambient Occlusion)
// WebGPU: Uses GTAO (Ground Truth Ambient Occlusion)

/**
 * Ambient occlusion darkens creases and contact regions. WebGL uses SSAO fields; WebGPU uses GTAO fields; shared keys apply to both where supported.
 */
export interface AOEffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.AO;

  /** AO bias (eliminates artifacts from depth discontinuities) */
  bias?: number;
  /** Resolution scale for internal render targets */
  resolutionScale?: number;

  // SSAO-specific parameters (WebGL only)
  /** SSAO strength/intensity [0-4] */
  ssaoStrength?: number;
  /** SSAO sampling radius [0-1] */
  ssaoRadius?: number;
  /** Number of SSAO samples [1-32] */
  ssaoSamples?: number;
  /** Smoothness of shadows (lower = higher contrast) - SSAO only */
  fade?: number;
  /** Luminance influence on AO - SSAO only */
  luminanceInfluence?: number;
  /** Minimum radius scale - SSAO only */
  minRadiusScale?: number;
  /** Depth-aware upsampling - SSAO only */
  depthAwareUpsampling?: boolean;
  /** World distance threshold for AO fadeout - SSAO only */
  worldDistanceThreshold?: number;
  /** World distance falloff smoothness - SSAO only */
  worldDistanceFalloff?: number;
  /** World proximity threshold for AO fadeout - SSAO only */
  worldProximityThreshold?: number;
  /** World proximity falloff smoothness - SSAO only */
  worldProximityFalloff?: number;
  /** AO color (null = grayscale) - SSAO only */
  color?: number | null;

  // GTAO-specific parameters (WebGPU only)
  /** GTAO strength/intensity [0-2] */
  gtaoStrength?: number;
  /** GTAO sampling radius in world units [0.1-1.0] */
  gtaoRadius?: number;
  /** Number of GTAO samples [1-32] */
  gtaoSamples?: number;
  /** AO thickness - GTAO only */
  thickness?: number;
  /** Distance exponent for AO falloff - GTAO only */
  distanceExponent?: number;
  /** Distance falloff for AO - GTAO only */
  distanceFallOff?: number;
}

// ============= Tone Mapping Effect =============

/**
 * Maps HDR scene luminance to display range and applies exposure. WebGL supports additional auto-exposure tuning fields.
 */
export interface ToneMappingEffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.ToneMapping;
  /** Tone mapping mode */
  mode?: THREE.ToneMapping;
  /** Exposure value */
  exposure?: number;
  /** Skip objects with materials that have toneMapped set to false (WebGPU only) */
  skipNonToneMappedMaterials?: boolean;
  /** Resolution of luminance texture (WebGL only) */
  resolution?: number;
  /** White point for Reinhard2 (WebGL only) */
  whitePoint?: number;
  /** Middle grey for Reinhard2 (WebGL only) */
  middleGrey?: number;
  /** Minimum luminance (WebGL only) */
  minLuminance?: number;
  /** Average luminance (WebGL only) */
  averageLuminance?: number;
  /** Luminance adaptation rate (WebGL only) */
  adaptationRate?: number;
}

// ============= Colour Grading Effect =============

/**
 * Display-referred colour grading controls intended to run after tone mapping.
 * Currently implemented for WebGPU.
 */
export interface ColorGradingEffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.ColorGrading;
  /** Global exposure multiplier. */
  exposure?: number;
  /** Global contrast around mid-grey. */
  contrast?: number;
  /** Global saturation multiplier. */
  saturation?: number;
  /** Global hue shift in degrees. */
  hueShift?: number;
  /** White-balance temperature offset (warm/cool). */
  temperature?: number;
  /** White-balance tint offset (magenta/green). */
  tint?: number;
  /** Lift amount for shadows. */
  lift?: number;
  /** Gamma pivot for midtones (1 = neutral). */
  gamma?: number;
  /** Gain multiplier for highlights. */
  gain?: number;
  /** Lift tint colour (hex). */
  liftTint?: number;
  /** Gamma tint colour (hex). */
  gammaTint?: number;
  /** Gain tint colour (hex). */
  gainTint?: number;
  /** Shadows tint colour (hex). */
  shadowsTint?: number;
  /** Shadows tint blend strength. */
  shadowsStrength?: number;
  /** Midtones tint colour (hex). */
  midtonesTint?: number;
  /** Midtones tint blend strength. */
  midtonesStrength?: number;
  /** Highlights tint colour (hex). */
  highlightsTint?: number;
  /** Highlights tint blend strength. */
  highlightsStrength?: number;
}

// ============= Anti-Aliasing Effect =============

/** SMAA quality preset indices (WebGL SMAA path only). */
export const AntiAliasingPreset = {
  /** Fastest, thinnest search patterns. */
  LOW: 0,
  /** Balanced quality and cost. */
  MEDIUM: 1,
  /** Default high-quality preset. */
  HIGH: 2,
  /** Highest search radius and edge treatment. */
  ULTRA: 3,
} as const;

export type AntiAliasingPresetType = typeof AntiAliasingPreset[keyof typeof AntiAliasingPreset];

/**
 * Full-screen anti-aliasing pass; SMAA preset applies on WebGL where that path is used.
 */
export interface AntiAliasingEffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.AntiAliasing;
  /** Preset quality (WebGL SMAA only) */
  preset?: AntiAliasingPresetType;
}

// ============= Object Outline Effect =============

/**
 * Renders contour lines around depth or ID discontinuities, with optional per-actor grouping to suppress internal edges.
 */
export interface ObjectOutlineEffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.ObjectOutline;
  /** Edge strength multiplier */
  edgeStrength?: number;
  /** Edge thickness in pixels */
  edgeThickness?: number;
  /** Visible edge color (hex) */
  visibleEdgeColor?: number;
  /** Hidden edge color (hex, WebGPU only) */
  hiddenEdgeColor?: number;
  /** Render hidden-edge contribution (WebGPU only) */
  showHiddenEdge?: boolean;
  /** Resolution scale for internal render targets */
  resolutionScale?: number;
  /** Use per-actor IDs (removes internal edges) */
  useActorGrouping?: boolean;
  /** Edge blur amount (0 = sharp) */
  edgeBlur?: number;
  /** Edge glow for pulse effects (WebGPU only) */
  edgeGlow?: number;
}

// ============= Additional WebGPU-focused Effects =============

/**
 * Depth-of-field using focus distance and focal length; `bokehScale` controls blur disc size.
 */
export interface DepthOfFieldEffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.DepthOfField;
  /** Distance from camera to focal plane (world or normalised, consistent with pipeline). */
  focusDistance?: number;
  /** Lens focal length used with depth to compute circle of confusion. */
  focalLength?: number;
  /** Multiplier on blur kernel / CoC radius. */
  bokehScale?: number;
  /**
   * When true (default), keep pixels nearer than the focal plane sharp and only blend the
   * DOF result for farther geometry. When false, use symmetric near and far blur like the stock Three.js node.
   */
  farOnly?: boolean;
}

/**
 * Quantises the image into `pixelSize` blocks and can emphasise edges using normals and depth.
 */
export interface PixelationEffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.Pixelation;
  /** Pixel block size in screen pixels. */
  pixelSize?: number;
  /** Weight of normal-based edge lines between blocks. */
  normalEdgeStrength?: number;
  /** Weight of depth-based edge lines between blocks. */
  depthEdgeStrength?: number;
}

/**
 * Composite-era look: colour banding, scanlines, vignette, and subtle barrel-style distortion.
 */
export interface RetroEffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.Retro;
  /** Quantisation steps per channel (lower = more banding). */
  colorDepthSteps?: number;
  /** Darkness / visibility of horizontal scanlines. */
  scanlineIntensity?: number;
  /** Scanline frequency relative to screen height. */
  scanlineDensity?: number;
  /** Animated scanline phase speed. */
  scanlineSpeed?: number;
  /** Edge darkening amount. */
  vignetteIntensity?: number;
  /** Colour bleed between scanlines or pixels. */
  bleeding?: number;
  /** Barrel / pincushion-style curvature strength. */
  curvature?: number;
  /** Affine shear-style screen warp. */
  affineDistortion?: number;
}

/**
 * Traces reflections in screen space; `quality` trades accuracy for performance, `thickness` avoids surface self-intersection.
 */
export interface SSREffectConfig extends BaseEffectConfig {
  type: typeof PostProcessPass.SSR;
  /** Overall tracing quality preset scalar (0–1 typical). */
  quality?: number;
  /** Blur passes or kernel quality for noisy reflections. */
  blurQuality?: number;
  /** Maximum trace distance in depth or world-normalised units (backend-specific). */
  maxDistance?: number;
  /** Blend factor of SSR over the base colour. */
  opacity?: number;
  /** Depth bias / surface thickness for ray hits. */
  thickness?: number;
  /** Internal SSR render scale (0.1-1, lower = faster with softer detail). */
  resolutionScale?: number;
}

// ============= Default Configurations =============

/**
 * Suggested authoring defaults per {@link PostProcessPass}. Merge into your stack or use as a baseline; not all backends honour every field.
 */
export const DefaultPostprocessEffectConfigs = {
  [PostProcessPass.Bloom]: {
    enabled: false,
    type: PostProcessPass.Bloom,
    strength: 2.0,
    threshold: 1.0,
    smoothing: 0.2,
    radius: 0.85,
    mipmapBlur: true,
    levels: 8,
    selectiveEmissive: false,
    ignoreBackground: true,
  } as BloomEffectConfig,

  [PostProcessPass.AO]: {
    enabled: false,
    type: PostProcessPass.AO,
    bias: 0.025,
    resolutionScale: 1.0,
    // SSAO-specific (WebGL) - radius [0-1], samples [1-32], strength [0-4]
    ssaoStrength: 1.33,
    ssaoRadius: 0.1,
    ssaoSamples: 9,
    fade: 0.01,
    luminanceInfluence: 0.7,
    minRadiusScale: 0.33,
    depthAwareUpsampling: true,
    worldDistanceThreshold: 0.02, // distanceThreshold in postprocessing
    worldDistanceFalloff: 0.0025, // distanceFalloff in postprocessing
    worldProximityThreshold: 0.0003, // rangeThreshold in postprocessing
    worldProximityFalloff: 0.0001, // rangeFalloff in postprocessing
    color: null,
    // GTAO-specific (WebGPU) - radius [0.1-1.0] world units, samples [1-32], strength [0-2]
    gtaoStrength: 1.0,
    gtaoRadius: 0.25,
    gtaoSamples: 16,
    thickness: 1.0,
    distanceExponent: 1.0,
    distanceFallOff: 1.0,
  } as AOEffectConfig,

  [PostProcessPass.ToneMapping]: {
    enabled: true,
    type: PostProcessPass.ToneMapping,
    mode: THREE.LinearToneMapping,
    exposure: 0.3,
    skipNonToneMappedMaterials: false,
    resolution: 256,
    whitePoint: 1.0,
    middleGrey: 0.6,
    minLuminance: 0.01,
    averageLuminance: 0.01,
    adaptationRate: 1.0,
  } as ToneMappingEffectConfig,

  [PostProcessPass.ColorGrading]: {
    enabled: false,
    type: PostProcessPass.ColorGrading,
    exposure: 1.0,
    contrast: 1.0,
    saturation: 1.0,
    hueShift: 0.0,
    temperature: 0.0,
    tint: 0.0,
    lift: 0.0,
    gamma: 1.0,
    gain: 1.0,
    liftTint: 0xffffff,
    gammaTint: 0xffffff,
    gainTint: 0xffffff,
    shadowsTint: 0xffffff,
    shadowsStrength: 0.0,
    midtonesTint: 0xffffff,
    midtonesStrength: 0.0,
    highlightsTint: 0xffffff,
    highlightsStrength: 0.0,
  } as ColorGradingEffectConfig,

  [PostProcessPass.AntiAliasing]: {
    enabled: true,
    type: PostProcessPass.AntiAliasing,
    preset: AntiAliasingPreset.HIGH,
  } as AntiAliasingEffectConfig,

  [PostProcessPass.ObjectOutline]: {
    enabled: false,
    type: PostProcessPass.ObjectOutline,
    edgeStrength: 3.0,
    edgeThickness: 1.0,
    visibleEdgeColor: 0xffffff, // white
    hiddenEdgeColor: 0x888888, // grey
    showHiddenEdge: true,
    resolutionScale: 1.0,
    useActorGrouping: true,
    edgeBlur: 0.2,
    edgeGlow: 0,
  } as ObjectOutlineEffectConfig,

  [PostProcessPass.DepthOfField]: {
    enabled: false,
    type: PostProcessPass.DepthOfField,
    focusDistance: 100,
    focalLength: 15,
    bokehScale: 3.5,
    farOnly: true,
  } as DepthOfFieldEffectConfig,

  [PostProcessPass.Pixelation]: {
    enabled: false,
    type: PostProcessPass.Pixelation,
    pixelSize: 6,
    normalEdgeStrength: 0.3,
    depthEdgeStrength: 0.4,
  } as PixelationEffectConfig,

  [PostProcessPass.Retro]: {
    enabled: false,
    type: PostProcessPass.Retro,
    colorDepthSteps: 32,
    scanlineIntensity: 0.3,
    scanlineDensity: 1,
    scanlineSpeed: 0,
    vignetteIntensity: 0.3,
    bleeding: 0.001,
    curvature: 0.02,
    affineDistortion: 0,
  } as RetroEffectConfig,

  [PostProcessPass.SSR]: {
    enabled: false,
    type: PostProcessPass.SSR,
    quality: 0.5,
    blurQuality: 1,
    maxDistance: 1,
    opacity: 1,
    thickness: 0.03,
    resolutionScale: 1,
  } as SSREffectConfig,
} as const;

/**
 * Root post-processing document: an ordered list of effect configs. Runtime order may be overridden per entry via {@link BaseEffectConfig.order} or backend effect `order`.
 */
export interface PostProcessConfiguration {
  /** Effect stack from first to last draw; combine entries from {@link DefaultPostprocessEffectConfigs} or custom configs. */
  effects: BaseEffectConfig[];
}
